/**
 * Offline Storage using IndexedDB
 * Stores offline data and syncs when connection is restored
 */

const DB_NAME = 'HealthLoopOffline';
const DB_VERSION = 1;
const STORES = {
  OFFLINE_QUEUE: 'offlineQueue',
  CACHED_DATA: 'cachedData',
  USER_DATA: 'userData'
};

// Initialize IndexedDB
export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object stores
      if (!db.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = db.createObjectStore(STORES.OFFLINE_QUEUE, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        queueStore.createIndex('timestamp', 'timestamp', { unique: false });
        queueStore.createIndex('type', 'type', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.CACHED_DATA)) {
        const cacheStore = db.createObjectStore(STORES.CACHED_DATA, { 
          keyPath: 'key' 
        });
        cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
        db.createObjectStore(STORES.USER_DATA, { keyPath: 'id' });
      }
    };
  });
};

// Generic DB operation helper
const performDBOperation = async (storeName, mode, operation) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([storeName], mode);
    const store = transaction.objectStore(storeName);
    
    return new Promise((resolve, reject) => {
      const request = operation(store);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      
      transaction.oncomplete = () => db.close();
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('DB operation failed:', error);
    throw error;
  }
};

// ==================== OFFLINE QUEUE ====================

// Add action to offline queue
export const addToOfflineQueue = async (action) => {
  const queueItem = {
    ...action,
    timestamp: new Date().toISOString(),
    status: 'pending'
  };

  return performDBOperation(STORES.OFFLINE_QUEUE, 'readwrite', (store) => {
    return store.add(queueItem);
  });
};

// Get all pending offline actions
export const getOfflineQueue = async () => {
  return performDBOperation(STORES.OFFLINE_QUEUE, 'readonly', (store) => {
    return store.getAll();
  });
};

// Remove action from offline queue
export const removeFromOfflineQueue = async (id) => {
  return performDBOperation(STORES.OFFLINE_QUEUE, 'readwrite', (store) => {
    return store.delete(id);
  });
};

// Clear entire offline queue
export const clearOfflineQueue = async () => {
  return performDBOperation(STORES.OFFLINE_QUEUE, 'readwrite', (store) => {
    return store.clear();
  });
};

// ==================== CACHED DATA ====================

// Cache data with a key
export const cacheData = async (key, data) => {
  const cacheItem = {
    key,
    data,
    timestamp: new Date().toISOString()
  };

  return performDBOperation(STORES.CACHED_DATA, 'readwrite', (store) => {
    return store.put(cacheItem);
  });
};

// Get cached data by key
export const getCachedData = async (key) => {
  const result = await performDBOperation(STORES.CACHED_DATA, 'readonly', (store) => {
    return store.get(key);
  });

  return result?.data || null;
};

// Get all cached data
export const getAllCachedData = async () => {
  return performDBOperation(STORES.CACHED_DATA, 'readonly', (store) => {
    return store.getAll();
  });
};

// Delete cached data by key
export const deleteCachedData = async (key) => {
  return performDBOperation(STORES.CACHED_DATA, 'readwrite', (store) => {
    return store.delete(key);
  });
};

// Clear all cached data
export const clearCachedData = async () => {
  return performDBOperation(STORES.CACHED_DATA, 'readwrite', (store) => {
    return store.clear();
  });
};

// ==================== USER DATA ====================

// Save user data offline
export const saveUserData = async (userId, userData) => {
  const userItem = {
    id: userId,
    data: userData,
    timestamp: new Date().toISOString()
  };

  return performDBOperation(STORES.USER_DATA, 'readwrite', (store) => {
    return store.put(userItem);
  });
};

// Get user data
export const getUserData = async (userId) => {
  const result = await performDBOperation(STORES.USER_DATA, 'readonly', (store) => {
    return store.get(userId);
  });

  return result?.data || null;
};

// ==================== HELPER FUNCTIONS ====================

// Check if data is stale (older than specified minutes)
export const isDataStale = (timestamp, maxAgeMinutes = 30) => {
  const now = new Date();
  const dataTime = new Date(timestamp);
  const diffMinutes = (now - dataTime) / (1000 * 60);
  
  return diffMinutes > maxAgeMinutes;
};

// Get cache size
export const getCacheSize = async () => {
  const allCached = await getAllCachedData();
  const sizeInBytes = new Blob([JSON.stringify(allCached)]).size;
  return {
    items: allCached.length,
    sizeKB: (sizeInBytes / 1024).toFixed(2),
    sizeMB: (sizeInBytes / (1024 * 1024)).toFixed(2)
  };
};

export default {
  initDB,
  addToOfflineQueue,
  getOfflineQueue,
  removeFromOfflineQueue,
  clearOfflineQueue,
  cacheData,
  getCachedData,
  getAllCachedData,
  deleteCachedData,
  clearCachedData,
  saveUserData,
  getUserData,
  isDataStale,
  getCacheSize
};
